import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import { completeImageUrl } from '../utils/imageUrl';
import { shopInitials } from '../utils/shopInitials';
import './SearchPanel.css';

const MIN_QUERY_LENGTH = 2;
const EMPTY = { products: [], shops: [] };

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Header search: a pill input plus a dropdown with matching shops and products as you type.
// Enter / "Показати всі результати" hands the query to onSubmitAll (the existing ?query= list).
export default function SearchPanel({ open, onClose, onSubmitAll }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const trimmed = query.trim();
  const searching = trimmed.length >= MIN_QUERY_LENGTH;

  // focus when opened, reset when closed
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(timer);
    }
    setQuery('');
    setResults(EMPTY);
    setLoading(false);
    return undefined;
  }, [open]);

  // debounced lookup; a stale response never overwrites a newer query
  useEffect(() => {
    if (!searching) {
      setResults(EMPTY);
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      axios
        .get('/api/v1/search', { params: { q: trimmed } })
        .then((response) => {
          if (!active) return;
          setResults({
            products: response.data?.products || [],
            shops: response.data?.shops || [],
          });
        })
        .catch((error) => {
          if (active) console.error('Search error:', error);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [trimmed, searching]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (trimmed) onSubmitAll(trimmed);
  };

  const openProduct = (product) => {
    navigate(`/products/${product.id}`);
    onClose();
  };

  const openShop = (shop) => {
    const handle = shop.name ? (shop.name.startsWith('@') ? shop.name : `@${shop.name}`) : null;
    navigate(handle ? `/${handle}` : `/account/${shop.id}`);
    onClose();
  };

  const hasResults = results.products.length > 0 || results.shops.length > 0;

  return (
    <>
      <div className={`search-panel__backdrop ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`search-panel ${open ? 'open' : ''}`} role="dialog" aria-label="Пошук">
        <div className="search-panel__inner">
          <form className="search-panel__bar" onSubmit={handleSubmit}>
            <span className="search-panel__icon"><SearchIcon /></span>
            <input
              ref={inputRef}
              type="search"
              className="search-panel__input"
              placeholder="Товари та магазини"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Пошук товарів і магазинів"
              autoComplete="off"
              enterKeyHint="search"
            />
            {query && (
              <button
                type="button"
                className="search-panel__clear"
                onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                aria-label="Очистити"
              >
                ×
              </button>
            )}
            <button type="button" className="search-panel__cancel" onClick={onClose}>Скасувати</button>
          </form>

          {searching && (
            <div className="search-panel__results">
              {loading && !hasResults && <p className="search-panel__hint">Шукаємо…</p>}
              {!loading && !hasResults && <p className="search-panel__hint">Нічого не знайдено</p>}

              {results.shops.length > 0 && (
                <div className="search-panel__section">
                  <span className="search-panel__label">Магазини</span>
                  {results.shops.map((shop) => (
                    <button key={`shop-${shop.id}`} type="button" className="search-panel__row" onClick={() => openShop(shop)}>
                      <span
                        className="search-panel__avatar"
                        style={shop.image ? { backgroundImage: `url(${completeImageUrl(shop.image)})` } : undefined}
                      >
                        {!shop.image && shopInitials(shop.title || shop.name)}
                      </span>
                      <span className="search-panel__text">
                        <span className="search-panel__title">{shop.title || shop.name}</span>
                        <span className="search-panel__sub">{shop.name}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {results.products.length > 0 && (
                <div className="search-panel__section">
                  <span className="search-panel__label">Товари</span>
                  {results.products.map((product) => (
                    <button key={`product-${product.id}`} type="button" className="search-panel__row" onClick={() => openProduct(product)}>
                      <span
                        className="search-panel__thumb"
                        style={product.image ? { backgroundImage: `url(${completeImageUrl(product.image)})` } : undefined}
                      />
                      <span className="search-panel__text">
                        <span className="search-panel__title">{product.title}</span>
                        <span className="search-panel__sub">
                          {[product.category, product.price ? `від ${product.price} ₴` : null].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {hasResults && (
                <button type="button" className="search-panel__all" onClick={() => onSubmitAll(trimmed)}>
                  Показати всі результати для «{trimmed}»
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
